/**
 * The automation sidebar's start page: an overview of what is set up, laid
 * out like the canvas automation block. On top the card of the whole
 * automation (trigger, sharing in the Hub, last run), then Input → Workflow →
 * Output joined by the block's arrow, and the pinned footer: one bar with the
 * status, the trigger's on/off switch and the history, and Run below it.
 * While something is still open the status pulses and is a disclosure: it
 * lists the open items, and an item opens its setting.
 *
 * The sidebar only shows; settings are made in the settings pane. Each card
 * is one toggle for its pane: a click opens the pane at the card's first open
 * setting, a second click closes it. A card lists its settings in use with
 * their values; a setting that still needs work names what is missing.
 */
import { AnimatedDisclosure } from '@affine/core/components/animation/animated-disclosure';
import { AnimatedRegion } from '@affine/core/components/animation/animated-region';
import { useI18n } from '@affine/i18n';
import { ArrowRightSmallIcon, HistoryIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { type FC, type ReactNode, useEffect, useId, useState } from 'react';

import { type ExecutionLog, LoggingService } from '../services/logging';
import type {
  AutomationReadiness,
  ReadinessIssue,
  ReadinessState,
  ReadinessTileId,
} from './automation-readiness';
import * as styles from './automation-start-page.css';
import { attentionPulse } from './components/attention-pulse.css';
import { FlowConnector } from './components/flow-connector';
import { formatHistoryExecutionStatusLabel } from './components/history-status-labels';
import { RunBars } from './components/run-bars';
import { settingProgress, useSettingProgressLabel } from './setting-progress';
import {
  PANE_PRIMARY_TILE,
  paneOfTile,
  type SettingsPane,
  tilesOfPane,
} from './settings-pane';

const STEP_PANES = ['input', 'workflow', 'output'] as const;

const STATE_LABEL_KEYS: Record<Exclude<ReadinessState, 'none'>, string> = {
  ok: 'com.streamlain.automation.panel.state.ok',
  missing: 'com.streamlain.automation.panel.state.missing',
  error: 'com.streamlain.automation.panel.state.error',
};

const STATE_RANK: Record<ReadinessState, number> = {
  none: 0,
  ok: 1,
  missing: 2,
  error: 3,
};

/** The worst state of a pane's settings: the card's dot. */
export const paneState = (
  pane: SettingsPane,
  readiness: Pick<AutomationReadiness, 'tiles'>
): ReadinessState =>
  tilesOfPane(pane).reduce<ReadinessState>(
    (worst, tile) =>
      STATE_RANK[readiness.tiles[tile]] > STATE_RANK[worst]
        ? readiness.tiles[tile]
        : worst,
    'none'
  );

/** The latest run in words and the last runs as bars, as on the block. */
export const LastRunSummary: FC<{ blockId: string; workspaceId: string }> = ({
  blockId,
  workspaceId,
}) => {
  const t = useI18n();
  const logging = useService(LoggingService);
  const logs = useLiveData(logging.getLogs$(blockId));
  const active = useLiveData(logging.getActiveLog$(blockId));
  const loading = useLiveData(logging.getLoadingState$(blockId));
  useEffect(() => {
    void logging.loadFromBackend(blockId, workspaceId);
  }, [blockId, workspaceId, logging]);
  const runs = [active, ...logs]
    .filter((log): log is ExecutionLog => log !== null)
    .sort((a, b) => b.startedAt - a.startedAt);
  const latest = runs[0];
  const when = (log: ExecutionLog) =>
    new Date(log.startedAt).toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    });

  return (
    <span className={styles.lastRun}>
      <span className={styles.lastRunText}>
        {latest ? (
          <>
            <time dateTime={new Date(latest.startedAt).toISOString()}>
              {when(latest)}
            </time>
            {` · ${formatHistoryExecutionStatusLabel(t, latest.status)}`}
          </>
        ) : (
          t.t(
            loading
              ? 'com.streamlain.automation.overview.history-loading'
              : 'com.streamlain.automation.overview.no-last-run'
          )
        )}
      </span>
      <RunBars
        logs={runs}
        describe={log =>
          `${when(log)} · ${formatHistoryExecutionStatusLabel(t, log.status)}`
        }
      />
    </span>
  );
};

export interface AutomationStartPageProps {
  readiness: AutomationReadiness;
  /** One-line value per set-up setting ("Tasks", "3 filters", …). */
  summaries: Record<ReadinessTileId, string>;
  /** The icon of each card (the general card's follows the trigger). */
  icons: Record<SettingsPane, ReactNode>;
  /** Whether and how the automation can be shared in the Hub, in words. */
  hubSummary: string;
  blockId: string;
  workspaceId: string;
  /** The pane open beside the sidebar. */
  activePane?: SettingsPane | null;
  /** Opens a card's pane, or closes it when it is open. */
  onTogglePane: (pane: SettingsPane) => void;
  /** The automation's status in words, above Run. */
  statusLabel: string;
  /** Opens the setting of an open item and points it out. */
  onOpenIssue: (issue: ReadinessIssue) => void;
  /**
   * The trigger's on/off switch, beside the status. Only a trigger that runs
   * by itself has one (schedule, new row); a manual run has nothing to switch.
   */
  activation?: ReactNode;
  /** Whether the pane shows the history. */
  historyOpen: boolean;
  onToggleHistory: () => void;
  /** Run, with its progress and results. */
  actions: ReactNode;
  /** Shown above the cards (e.g. a detected automation loop). */
  notice?: ReactNode;
}

interface CardRow {
  key: string;
  label: string;
  value: ReactNode;
  state: ReadinessState;
}

export const AutomationStartPage: FC<AutomationStartPageProps> = ({
  readiness,
  summaries,
  icons,
  hubSummary,
  blockId,
  workspaceId,
  activePane = null,
  onTogglePane,
  statusLabel,
  onOpenIssue,
  activation,
  historyOpen,
  onToggleHistory,
  actions,
  notice,
}) => {
  const t = useI18n();
  const progressLabel = useSettingProgressLabel();
  const idPrefix = useId();
  const [openItemsShown, setOpenItemsShown] = useState(false);
  const issueOf = (tile: ReadinessTileId): ReadinessIssue | undefined =>
    readiness.issues.find(issue => issue.tile === tile);

  /** A setting in use: its value, or what is missing in its place. */
  const tileRow = (tile: ReadinessTileId): CardRow => {
    const issue = issueOf(tile);
    return {
      key: tile,
      label: t.t(`com.streamlain.automation.panel.tile.${tile}`),
      value: issue ? t.t(issue.labelKey) : summaries[tile],
      state: readiness.tiles[tile],
    };
  };

  const rowsOf = (pane: SettingsPane): CardRow[] => {
    if (pane === 'general') {
      const history = issueOf('history');
      return [
        tileRow('trigger'),
        {
          key: 'hub',
          label: t['com.streamlain.automation.panel.hub.label'](),
          value: hubSummary,
          state: 'none',
        },
        {
          key: 'history',
          label: t['com.streamlain.automation.panel.last-run'](),
          value: history ? (
            t.t(history.labelKey)
          ) : (
            <LastRunSummary blockId={blockId} workspaceId={workspaceId} />
          ),
          state: readiness.tiles.history,
        },
      ];
    }
    const primary = PANE_PRIMARY_TILE[pane];
    return tilesOfPane(pane)
      .filter(tile => tile === primary || readiness.tiles[tile] !== 'none')
      .map(tileRow);
  };

  const renderCard = (pane: SettingsPane) => {
    const state = paneState(pane, readiness);
    const name = t.t(`com.streamlain.automation.panel.group.${pane}`);
    const titleId = `${idPrefix}-${pane}-title`;
    const bodyId = `${idPrefix}-${pane}-body`;
    const progress =
      pane === 'general'
        ? null
        : settingProgress(tilesOfPane(pane), readiness.tiles);
    const active = pane === activePane;

    return (
      <section
        className={styles.card}
        data-step={pane}
        data-state={state}
        data-active={active || undefined}
        aria-labelledby={titleId}
      >
        <button
          type="button"
          className={styles.cardToggle}
          aria-expanded={active}
          aria-labelledby={titleId}
          aria-describedby={bodyId}
          onClick={() => onTogglePane(pane)}
          data-testid={`automation-card-${pane}`}
        />
        <div className={styles.cardHead}>
          <span className={styles.cardIcon} aria-hidden="true">
            {icons[pane]}
          </span>
          <div className={styles.cardHeading}>
            <h3 id={titleId} className={styles.cardTitle}>
              {name}
            </h3>
            {progress && progress.total > 0 && (
              <p
                className={styles.cardProgress}
                data-open={progress.open > 0 || undefined}
                data-testid="automation-step-progress"
              >
                {progressLabel(progress)}
              </p>
            )}
          </div>
          {state !== 'none' && (
            <span
              className={styles.stateDot}
              data-state={state}
              role="img"
              aria-label={t.t(STATE_LABEL_KEYS[state])}
              title={t.t(STATE_LABEL_KEYS[state])}
            />
          )}
        </div>
        <AnimatedRegion expanded animateContent id={bodyId}>
          <dl className={styles.rows}>
            {rowsOf(pane).map(row => (
              <div
                key={row.key}
                className={styles.row}
                data-state={row.state}
                data-row={row.key}
              >
                <dt className={styles.rowLabel} title={row.label}>
                  {row.label}
                </dt>
                <dd className={styles.rowValue}>{row.value}</dd>
              </div>
            ))}
          </dl>
        </AnimatedRegion>
      </section>
    );
  };

  const statusProps = {
    'data-status': readiness.status,
    'data-testid': 'automation-status',
  };
  const statusContent = (
    <>
      <span className={styles.statusDot[readiness.status]} />
      <span className={styles.statusLabel}>{statusLabel}</span>
    </>
  );
  const footerControls = (
    <div className={styles.footerControls}>
      {activation}
      <button
        type="button"
        className={styles.footerIconButton}
        aria-pressed={historyOpen}
        aria-label={t['com.streamlain.streamlain.execution.history']()}
        title={t['com.streamlain.streamlain.execution.history']()}
        onClick={onToggleHistory}
        data-testid="automation-history-toggle"
      >
        <HistoryIcon />
      </button>
    </div>
  );

  return (
    <div className={styles.startPage}>
      <div className={styles.startScroll}>
        {notice}
        <div className={styles.cards}>
          {renderCard('general')}
          <ol className={styles.flow}>
            {STEP_PANES.map((pane, index) => (
              <li key={pane} className={styles.flowItem}>
                {renderCard(pane)}
                {index < STEP_PANES.length - 1 && <FlowConnector />}
              </li>
            ))}
          </ol>
        </div>
      </div>

      <div className={styles.footer} data-testid="automation-start-footer">
        {readiness.issues.length > 0 ? (
          <AnimatedDisclosure
            headerClassName={styles.footerStatus}
            headerProps={statusProps}
            triggerClassName={clsx(
              styles.statusPillToggle,
              // It asks for a click until the open items are in view.
              readiness.status === 'setup' && !openItemsShown && attentionPulse
            )}
            title={statusContent}
            actions={footerControls}
            expanded={openItemsShown}
            onExpandedChange={setOpenItemsShown}
          >
            <ul
              className={styles.openItems}
              data-testid="automation-open-items"
            >
              {readiness.issues.map(issue => (
                <li key={`${issue.tile}:${issue.code}`}>
                  <button
                    type="button"
                    className={styles.openItem}
                    data-severity={issue.severity}
                    data-issue={issue.code}
                    onClick={() => onOpenIssue(issue)}
                  >
                    <span className={styles.openItemDot} aria-hidden="true" />
                    <span className={styles.openItemText}>
                      <span className={styles.openItemLabel}>
                        {t.t(issue.labelKey)}
                      </span>
                      <span className={styles.openItemPlace}>
                        {`${t.t(
                          `com.streamlain.automation.panel.group.${paneOfTile(issue.tile)}`
                        )} · ${t.t(
                          `com.streamlain.automation.panel.tile.${issue.tile}`
                        )}`}
                      </span>
                    </span>
                    <ArrowRightSmallIcon aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </AnimatedDisclosure>
        ) : (
          <div {...statusProps} className={styles.footerStatus}>
            <span className={styles.statusPill}>{statusContent}</span>
            {footerControls}
          </div>
        )}
        {actions}
      </div>
    </div>
  );
};
